import { createContext, useContext, useEffect } from 'react'
import { useImmer } from 'use-immer'

const noop = () => {}

// Defaults make the table non-selectable when no provider is present (e.g. the
// detail pin panel in the connection view).
const PinSelectionContext = createContext({
  selectable: false,
  selectedPins: [],
  onSelectedPinsChange: noop,
  onDetailsClick: noop,
  showDetail: false,
  reset: noop,
})

const samePins = (a, b) =>
  a.length === b.length && a.every((pin, index) => pin === b[index])

// Holds connector pin-selection state (which pins are checked + whether the
// detail diagram is shown) and exposes it to descendants without prop-drilling.
// `selectable` is supplied by the caller.
//
// `resetKey` identifies what the selection belongs to — the connector, plus any
// pins a deep link asked to focus. Whenever it changes the state is re-seeded
// from `seedPins`/`seedShowDetail`, so picking a different connector starts
// clean and a deep link opens straight on its pins. Seeding on change rather
// than on mount means the caller does not have to remount the subtree (which
// would take the devices/connectors table's search and paging down with it).
export function PinSelectionProvider({
  selectable = false,
  resetKey = null,
  seedPins = [],
  seedShowDetail = false,
  children,
}) {
  const [state, updateState] = useImmer({
    selectedPins: seedPins,
    showDetail: seedShowDetail,
  })

  // Guarded so an unchanged seed produces no new state — the effect also runs on
  // mount, and again on StrictMode's second pass.
  useEffect(() => {
    updateState((draft) => {
      if (!samePins(draft.selectedPins, seedPins)) draft.selectedPins = seedPins
      if (draft.showDetail !== seedShowDetail) draft.showDetail = seedShowDetail
    })
  }, [resetKey])

  const value = {
    selectable,
    selectedPins: state.selectedPins,
    onSelectedPinsChange: (pins) =>
      updateState((draft) => {
        draft.selectedPins = pins
      }),
    onDetailsClick: () =>
      updateState((draft) => {
        draft.showDetail = true
      }),
    showDetail: state.showDetail,
    reset: () =>
      updateState((draft) => {
        draft.selectedPins = []
        draft.showDetail = false
      }),
  }

  return (
    <PinSelectionContext.Provider value={value}>
      {children}
    </PinSelectionContext.Provider>
  )
}

export const usePinSelection = () => useContext(PinSelectionContext)
